import React, { useState, useEffect } from 'react';
import { TrendingUp, AlertCircle, Globe, Shield, Package, Download } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '';

const SpendByManufacturer = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      // Use available endpoint instead of non-existent report endpoint
      const response = await fetch(`${API_URL}/api/trm/stats/by-manufacturer`);
      if (!response.ok) throw new Error('Failed to fetch data');
      const manufacturers = await response.json();

      // Transform data to match expected format
      const result = {
        summary: {
          total_manufacturers: manufacturers.length,
          total_products: manufacturers.reduce((sum, m) => sum + m.product_count, 0),
          total_versions: manufacturers.reduce((sum, m) => sum + m.version_count, 0)
        },
        top_manufacturers: manufacturers.slice(0, 20).map(m => ({
          manufacturer_id: m.manufacturer_id,
          manufacturer_name: m.manufacturer_name,
          product_count: m.product_count,
          version_count: m.version_count,
          categories: m.categories || []
        }))
      };

      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center' }}>Loading spend analysis...</div>;
  if (error) return <div style={{ padding: '40px', color: 'var(--cares-orange)' }}>Error: {error}</div>;
  if (!data) return null;

  const { summary, top_manufacturers } = data;

  // Calculate max for chart scaling
  const maxProducts = Math.max(...top_manufacturers.map(m => m.product_count));

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: 'var(--cares-text-2)', marginBottom: '8px' }}>
          Vendor Portfolio Analysis
        </h1>
        <p style={{ fontSize: '14px', color: 'var(--cares-text-label)' }}>
          Manufacturer footprint, risk assessment, and consolidation opportunities
        </p>
      </div>

      {/* Summary Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '20px',
        marginBottom: '32px'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, var(--cares-purple) 0%, var(--cares-purple) 100%)',
          borderRadius: '12px',
          padding: '24px',
          color: 'var(--cares-text-max)',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <Package size={24} />
            <span style={{ fontSize: '14px', opacity: 0.9 }}>Total Vendors</span>
          </div>
          <div style={{ fontSize: '36px', fontWeight: 'bold', marginBottom: '8px' }}>
            {summary.total_manufacturers}
          </div>
          <div style={{ fontSize: '13px', opacity: 0.8 }}>
            {summary.total_products} products across portfolio
          </div>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, var(--cares-magenta) 0%, var(--cares-magenta) 100%)',
          borderRadius: '12px',
          padding: '24px',
          color: 'var(--cares-text-max)',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <AlertCircle size={24} />
            <span style={{ fontSize: '14px', opacity: 0.9 }}>High Risk Vendors</span>
          </div>
          <div style={{ fontSize: '36px', fontWeight: 'bold', marginBottom: '8px' }}>
            {summary.high_risk_vendors}
          </div>
          <div style={{ fontSize: '13px', opacity: 0.8 }}>
            C-SCRM assessment: High
          </div>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, var(--cares-blue) 0%, var(--cares-teal) 100%)',
          borderRadius: '12px',
          padding: '24px',
          color: 'var(--cares-text-max)',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <Globe size={24} />
            <span style={{ fontSize: '14px', opacity: 0.9 }}>Foreign Owned</span>
          </div>
          <div style={{ fontSize: '36px', fontWeight: 'bold', marginBottom: '8px' }}>
            {summary.foreign_owned_vendors}
          </div>
          <div style={{ fontSize: '13px', opacity: 0.8 }}>
            {((summary.foreign_owned_vendors / summary.total_manufacturers) * 100).toFixed(1)}% of vendors
          </div>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, var(--cares-green) 0%, var(--cares-teal) 100%)',
          borderRadius: '12px',
          padding: '24px',
          color: 'var(--cares-text-max)',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <Shield size={24} />
            <span style={{ fontSize: '14px', opacity: 0.9 }}>Avg Products/Vendor</span>
          </div>
          <div style={{ fontSize: '36px', fontWeight: 'bold', marginBottom: '8px' }}>
            {(summary.total_products / summary.total_manufacturers).toFixed(1)}
          </div>
          <div style={{ fontSize: '13px', opacity: 0.8 }}>
            Vendor diversity metric
          </div>
        </div>
      </div>

      {/* Top 10 Chart */}
      <div style={{
        background: 'var(--cares-surface-card)',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: '600', color: 'var(--cares-text-2)' }}>
            Top 10 Vendors by Product Count
          </h2>
          <button
            onClick={() => alert('Export functionality coming soon!')}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              border: '1px solid var(--cares-line-strong)',
              background: 'var(--cares-surface-card)',
              color: 'var(--cares-text-label)',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <Download size={16} />
            Export
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {top_manufacturers.map((mfr, idx) => (
            <div key={idx}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--cares-text-label)', width: '30px' }}>
                    #{idx + 1}
                  </span>
                  <span style={{ fontSize: '15px', fontWeight: '500', color: 'var(--cares-text-2)' }}>
                    {mfr.manufacturer_name}
                  </span>
                  {mfr.foreign_owned && (
                    <Globe size={14} color="var(--cares-amber)" title="Foreign Owned" />
                  )}
                  {mfr.cscrm_assessment === 'High' && (
                    <AlertCircle size={14} color="var(--cares-orange)" title="High C-SCRM Risk" />
                  )}
                </div>
                <div style={{ display: 'flex', gap: '16px', fontSize: '13px', color: 'var(--cares-text-label)' }}>
                  <span><strong>{mfr.product_count}</strong> products</span>
                  <span><strong>{mfr.version_count}</strong> versions</span>
                  {mfr.fedramp_products > 0 && (
                    <span style={{ color: 'var(--cares-green)' }}>
                      <Shield size={12} style={{ display: 'inline', marginRight: '4px' }} />
                      {mfr.fedramp_products} FedRAMP
                    </span>
                  )}
                </div>
              </div>
              
              {/* Progress bar */}
              <div style={{
                background: 'var(--cares-surface-board)',
                borderRadius: '4px',
                height: '8px',
                overflow: 'hidden'
              }}>
                <div style={{
                  background: `linear-gradient(90deg, var(--cares-blue) 0%, var(--cares-purple) 100%)`,
                  height: '100%',
                  width: `${(mfr.product_count / maxProducts) * 100}%`,
                  transition: 'width 0.3s ease'
                }} />
              </div>
              
              {mfr.categories && (
                <div style={{ fontSize: '12px', color: 'var(--cares-text-note)', marginTop: '4px', fontStyle: 'italic' }}>
                  Categories: {mfr.categories}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SpendByManufacturer;
